import {
  ActionRes,
  ReportKey,
  ReportsData,
  ReportsDataMap,
  ReportsFilterOptionsResponse,
  ReportsFilters,
} from '@models/interfaces';
import {
  ReportsActions,
  ReportsDoneActionPayload,
  ReportsRequestActionPayload,
} from '../actions/reports.actions';

type ActionWithType = ((payload?: unknown) => ActionRes) & { type: string };

export interface ReportsState {
  data: Partial<ReportsDataMap>;
  errors: Partial<Record<ReportKey, boolean>>;
  filterOptions: ReportsFilterOptionsResponse | null;
  filterOptionsError: boolean;
  filterOptionsLoading: boolean;
  filters: ReportsFilters;
  loadedReports: Partial<Record<ReportKey, string>>;
  loadingReportHashes: Partial<Record<ReportKey, string>>;
  loadingReports: Partial<Record<ReportKey, boolean>>;
  selectedReport: ReportKey;
}

const initialState: ReportsState = {
  data: {},
  errors: {},
  filterOptions: null,
  filterOptionsError: false,
  filterOptionsLoading: false,
  filters: {
    page: 1,
    size: 25,
  },
  loadedReports: {},
  loadingReportHashes: {},
  loadingReports: {},
  selectedReport: ReportKey.OverdueTasks,
};

const getSortedFilterValue = (value: ReportsFilters[keyof ReportsFilters]) =>
  Array.isArray(value) ? [...value].sort() : value;

const getActionType = (action: unknown): string =>
  (action as ActionWithType).type;

const isReportsRequestActionPayload = (
  payload: unknown,
): payload is ReportsRequestActionPayload =>
  !!payload &&
  typeof payload === 'object' &&
  typeof (payload as ReportsRequestActionPayload).requestHash === 'string';

const isReportsDoneActionPayload = (
  payload: unknown,
): payload is ReportsDoneActionPayload =>
  isReportsRequestActionPayload(payload) && 'data' in payload;

export const getReportsFilterHash = (filters: ReportsFilters = {}): string =>
  JSON.stringify(
    Object.keys(filters)
      .sort()
      .reduce((acc: ReportsFilters, key: string) => {
        const value = filters[key as keyof ReportsFilters];

        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && !value.length)
        ) {
          return acc;
        }

        return {
          ...acc,
          [key]: getSortedFilterValue(value),
        };
      }, {}),
  );

const reportStartActionTypes: Record<string, ReportKey> = {
  [getActionType(ReportsActions.getDatesAtAGlance.start)]:
    ReportKey.DatesAtAGlance,
  [getActionType(ReportsActions.getNewClosedProjects.start)]:
    ReportKey.NewClosedProjects,
  [getActionType(ReportsActions.getOverdueTasks.start)]: ReportKey.OverdueTasks,
  [getActionType(ReportsActions.getProjectDuration.start)]:
    ReportKey.ProjectDuration,
  [getActionType(ReportsActions.getStructuralPassFail.start)]:
    ReportKey.StructuralPassFail,
};

const reportDoneActionTypes: Record<string, ReportKey> = {
  [getActionType(ReportsActions.getDatesAtAGlance.done)]:
    ReportKey.DatesAtAGlance,
  [getActionType(ReportsActions.getNewClosedProjects.done)]:
    ReportKey.NewClosedProjects,
  [getActionType(ReportsActions.getOverdueTasks.done)]: ReportKey.OverdueTasks,
  [getActionType(ReportsActions.getProjectDuration.done)]:
    ReportKey.ProjectDuration,
  [getActionType(ReportsActions.getStructuralPassFail.done)]:
    ReportKey.StructuralPassFail,
};

const reportFailActionTypes: Record<string, ReportKey> = {
  [getActionType(ReportsActions.getDatesAtAGlance.fail)]:
    ReportKey.DatesAtAGlance,
  [getActionType(ReportsActions.getNewClosedProjects.fail)]:
    ReportKey.NewClosedProjects,
  [getActionType(ReportsActions.getOverdueTasks.fail)]: ReportKey.OverdueTasks,
  [getActionType(ReportsActions.getProjectDuration.fail)]:
    ReportKey.ProjectDuration,
  [getActionType(ReportsActions.getStructuralPassFail.fail)]:
    ReportKey.StructuralPassFail,
};

export const reducer = (
  incomingState: ReportsState | undefined,
  { payload, type }: ActionRes,
): ReportsState => {
  const state = incomingState ?? initialState;
  const reportStartKey = reportStartActionTypes[type];
  const reportDoneKey = reportDoneActionTypes[type];
  const reportFailKey = reportFailActionTypes[type];

  switch (type) {
    case getActionType(ReportsActions.getFilterOptions.start):
      return {
        ...state,
        filterOptionsError: false,
        filterOptionsLoading: true,
      };
    case getActionType(ReportsActions.getFilterOptions.done):
      return {
        ...state,
        filterOptions: payload as ReportsFilterOptionsResponse,
        filterOptionsError: false,
        filterOptionsLoading: false,
      };
    case getActionType(ReportsActions.getFilterOptions.fail):
      return {
        ...state,
        filterOptionsError: true,
        filterOptionsLoading: false,
      };
    case getActionType(ReportsActions.invalidateReportsData):
      return {
        ...state,
        data: {},
        errors: {},
        loadedReports: {},
        loadingReportHashes: {},
        loadingReports: {},
      };
    case getActionType(ReportsActions.setFilters):
      return {
        ...state,
        data: {},
        errors: {},
        filters: payload as ReportsFilters,
        loadedReports: {},
      };
    case getActionType(ReportsActions.setSelectedReport):
      return {
        ...state,
        selectedReport: payload as ReportKey,
      };
    default:
      break;
  }

  if (reportStartKey) {
    if (!isReportsRequestActionPayload(payload)) {
      return state;
    }

    return {
      ...state,
      errors: {
        ...state.errors,
        [reportStartKey]: false,
      },
      loadingReportHashes: {
        ...state.loadingReportHashes,
        [reportStartKey]: payload.requestHash,
      },
      loadingReports: {
        ...state.loadingReports,
        [reportStartKey]: true,
      },
    };
  }

  if (reportDoneKey) {
    if (!isReportsDoneActionPayload(payload)) {
      return state;
    }

    const isLatestRequest =
      state.loadingReportHashes[reportDoneKey] === payload.requestHash;
    const isCurrentRequest =
      getReportsFilterHash(state.filters) === payload.requestHash;

    return {
      ...state,
      ...(isCurrentRequest
        ? {
            data: {
              ...state.data,
              [reportDoneKey]: payload.data as ReportsData,
            },
            errors: {
              ...state.errors,
              [reportDoneKey]: false,
            },
            loadedReports: {
              ...state.loadedReports,
              [reportDoneKey]: payload.requestHash,
            },
          }
        : {}),
      ...(isLatestRequest
        ? {
            loadingReportHashes: {
              ...state.loadingReportHashes,
              [reportDoneKey]: undefined,
            },
            loadingReports: {
              ...state.loadingReports,
              [reportDoneKey]: false,
            },
          }
        : {}),
    };
  }

  if (reportFailKey) {
    if (!isReportsRequestActionPayload(payload)) {
      return state;
    }

    const isLatestRequest =
      state.loadingReportHashes[reportFailKey] === payload.requestHash;
    const isCurrentRequest =
      getReportsFilterHash(state.filters) === payload.requestHash;

    return {
      ...state,
      ...(isCurrentRequest
        ? {
            errors: {
              ...state.errors,
              [reportFailKey]: true,
            },
          }
        : {}),
      ...(isLatestRequest
        ? {
            loadingReportHashes: {
              ...state.loadingReportHashes,
              [reportFailKey]: undefined,
            },
            loadingReports: {
              ...state.loadingReports,
              [reportFailKey]: false,
            },
          }
        : {}),
    };
  }

  return state;
};
